import React, { useState, useMemo, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Tabs, theme, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { SearchOutlined, MoreOutlined } from '@ant-design/icons';
import { useStores } from '@/hooks/use-stores';
import AssistantInsightCard, { ActionButton, AdditionalAction } from './AssistantInsightCard';
import { useInsights } from './hooks/useInsights';
import { InsightStatus, CurrencySymbol } from '@/constants/enums';
import IconButton from '@/comp/common/IconButton';
import SortIcon from '@/comp/common/icons/SortIcon';
import FilterIcon from '@/comp/common/icons/FilterIcon';
import CreateNewInsightIcon from '@/comp/common/icons/CreateNewInsightIcon';
import NewConversationIcon from '@/comp/common/icons/NewConversationIcon';
import EmptyInsightsState from './EmptyInsightsState';
import * as S from './AssistantInsights.styles';

const { useToken } = theme;

interface AssistantInsightsProps {
  onInsightClick: (insightId: string) => void;
  onReviewPublish?: (insightId: string) => void;
  onEditInsight?: (insightId: string) => void;
  onNewConversation?: () => void;
  onPromptClick?: (prompt: string) => void;
}

const AssistantInsights: React.FC<AssistantInsightsProps> = observer(
  ({ onInsightClick, onReviewPublish, onEditInsight, onNewConversation, onPromptClick }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const { insightsStore, stationStore, devicesStore } = useStores();
    const [activeTab, setActiveTab] = useState<string>('insight');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const [headerHeight, setHeaderHeight] = useState<number>(0);
    const [isHeaderStuck, setIsHeaderStuck] = useState<boolean>(false);
    const [hasBeenStuck, setHasBeenStuck] = useState<boolean>(false);

    const {
      draftStationInsights,
      publishedStationInsights,
      archivedStationInsights,
      isLoading: insightsLoading,
    } = useInsights({ insightsStore, stationStore, devicesStore });

    const handleTabChange = (key: string) => {
      setActiveTab(key);
    };

    const handleInsightCardClick = (insightId: string) => {
      onInsightClick(insightId);
    };

    const currentInsights =
      activeTab === 'insight'
        ? draftStationInsights
        : activeTab === 'published'
        ? publishedStationInsights
        : archivedStationInsights;

    const tabItems = [
      {
        key: 'insight',
        label: t('AvailableInsightsCount', { count: draftStationInsights.length }),
      },
      {
        key: 'published',
        label: t('Published'),
      },
      {
        key: 'archived',
        label: t('Archived'),
      },
    ];

    // Extract currency symbol from first insight's value
    const currencySymbol = useMemo(() => {
      if (currentInsights.length === 0) return CurrencySymbol.USD;
      const firstValue = currentInsights[0]?.value || '';
      // Check if value starts with a currency symbol
      for (const symbol of Object.values(CurrencySymbol)) {
        if (firstValue.startsWith(symbol)) return symbol;
      }
      // Default to USD if no symbol found
      return CurrencySymbol.USD;
    }, [currentInsights]);

    // Calculate total value for insights section
    const totalValue = useMemo(() => {
      return currentInsights.reduce((sum, insight) => {
        const value = insight.value?.replace(/[^0-9.]/g, '');
        return sum + (parseFloat(value || '0') || 0);
      }, 0);
    }, [currentInsights]);

    // Calculate value breakdown by type
    const valueByType = useMemo(() => {
      const breakdown = { risk: 0, maintenance: 0, energy: 0, other: 0 };
      currentInsights.forEach(insight => {
        const value = insight.value?.replace(/[^0-9.]/g, '');
        const numValue = parseFloat(value || '0') || 0;
        breakdown[insight.type] = (breakdown[insight.type] || 0) + numValue;
      });
      return breakdown;
    }, [currentInsights]);

    const countByType = useMemo(() => {
      const counts = { risk: 0, maintenance: 0, energy: 0, other: 0 };
      currentInsights.forEach(insight => {
        counts[insight.type] = (counts[insight.type] || 0) + 1;
      });
      return counts;
    }, [currentInsights]);

    // Measure header height for sticky positioning
    useEffect(() => {
      const updateHeaderHeight = () => {
        if (headerRef.current) {
          setHeaderHeight(headerRef.current.offsetHeight);
        }
      };

      updateHeaderHeight();
      window.addEventListener('resize', updateHeaderHeight);
      return () => window.removeEventListener('resize', updateHeaderHeight);
    }, [activeTab, currentInsights.length]);

    // Detect when header becomes stuck using IntersectionObserver on sentinel element
    useEffect(() => {
      if (!sentinelRef.current) return;

      let rafId: number | null = null;
      let lastStuckState = false;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            // Header is stuck when sentinel is not visible (scrolled past the top)
            const stuck = !entry.isIntersecting;

            // Throttle updates using requestAnimationFrame to prevent shivering
            if (rafId !== null) {
              cancelAnimationFrame(rafId);
            }

            rafId = requestAnimationFrame(() => {
              if (stuck !== lastStuckState) {
                setIsHeaderStuck(stuck);
                if (stuck) {
                  setHasBeenStuck(true);
                }
                lastStuckState = stuck;
              }
            });
          });
        },
        {
          threshold: 0,
          rootMargin: '0px',
        },
      );

      observer.observe(sentinelRef.current);

      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        observer.disconnect();
      };
    }, []);

    return (
      <>
        <S.InsightsContainer ref={containerRef}>
          <S.SentinelElement ref={sentinelRef} />
          <S.StickyHeaderSection ref={headerRef}>
            <S.InsightsHeader>
              <S.InsightsTitleRow>
                <S.InsightsTitleGroup>
                  <S.InsightsTitle>{t('StationInsights')}</S.InsightsTitle>
                  <S.PendingValue>
                    <S.PendingValueLabel>{t('PendingPotentialValue')}</S.PendingValueLabel>
                    <S.PendingValueChip>
                      {currencySymbol}
                      {totalValue.toLocaleString()}
                    </S.PendingValueChip>
                  </S.PendingValue>
                </S.InsightsTitleGroup>
                {hasBeenStuck && (
                  <S.StickyHeaderButtons $isVisible={isHeaderStuck}>
                    <S.HeaderButton
                      onClick={() => {
                        // TODO: Implement create new insight functionality
                        console.log('Create New Insight clicked');
                      }}
                    >
                      <S.ButtonIcon>
                        <CreateNewInsightIcon width={24} height={24} />
                      </S.ButtonIcon>
                      <S.ButtonText>{t('CreateNewInsight')}</S.ButtonText>
                    </S.HeaderButton>
                    <S.HeaderButton
                      onClick={() => {
                        onNewConversation?.();
                      }}
                    >
                      <S.ButtonIcon>
                        <NewConversationIcon width={24} height={24} />
                      </S.ButtonIcon>
                      <S.ButtonText>{t('NewConversation')}</S.ButtonText>
                    </S.HeaderButton>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'stationOverview',
                            label: t('GetStationOverview'),
                            onClick: () => {
                              onNewConversation?.();
                              onPromptClick?.(t('StationOverviewPrompt'));
                            },
                          },
                          {
                            key: 'quarterlyMeeting',
                            label: t('PrepareForQuarterlyMeeting'),
                            onClick: () => {
                              onNewConversation?.();
                              onPromptClick?.(t('QuarterlyMeetingPrompt'));
                            },
                          },
                        ] as MenuProps['items'],
                      }}
                      trigger={['click']}
                    >
                      <S.ThreeDotsButton>
                        <MoreOutlined style={{ fontSize: 20 }} />
                      </S.ThreeDotsButton>
                    </Dropdown>
                  </S.StickyHeaderButtons>
                )}
              </S.InsightsTitleRow>
            </S.InsightsHeader>

            <S.TabsContainer>
              <Tabs items={tabItems} activeKey={activeTab} onChange={handleTabChange} defaultActiveKey="insight" />
            </S.TabsContainer>

            <S.TabsHeader>
              <S.TabsHeaderFilters>
                <IconButton
                  icon={<SortIcon width={18} height={18} />}
                  label="Sort"
                  showDropdown={true}
                  className="no-padding-left"
                  onClick={() => {
                    // TODO: Implement sort functionality
                  }}
                />
                <IconButton
                  icon={<FilterIcon width={18} height={18} />}
                  showDropdown={true}
                  className="no-padding-left"
                  onClick={() => {
                    // TODO: Implement filter functionality
                  }}
                />
                <IconButton
                  icon={<SearchOutlined style={{ fontSize: '14px' }} />}
                  label="Search"
                  className="no-padding-left"
                  onClick={() => {
                    // TODO: Implement search functionality
                  }}
                />
              </S.TabsHeaderFilters>
              {currentInsights.length > 0 &&
                (countByType.risk > 0 || countByType.maintenance > 0 || countByType.energy > 0) && (
                  <S.ValueBreakdown>
                    <S.ValueBreakdownLabel>{t('ValueByType')}</S.ValueBreakdownLabel>
                    <S.ValueBreakdownItems>
                      {countByType.risk > 0 && (
                        <S.ValueBreakdownItem $type="risk">
                          <S.ValueBreakdownLine $type="risk" />
                          <S.ValueBreakdownText $type="risk">
                            {countByType.risk} {t('Risk')}
                          </S.ValueBreakdownText>
                          <S.ValueBreakdownChip>
                            {currencySymbol}
                            {valueByType.risk.toLocaleString()}
                          </S.ValueBreakdownChip>
                        </S.ValueBreakdownItem>
                      )}
                      {countByType.maintenance > 0 && (
                        <S.ValueBreakdownItem $type="maintenance">
                          <S.ValueBreakdownLine $type="maintenance" />
                          <S.ValueBreakdownText $type="maintenance">
                            {countByType.maintenance} {t('Maintenance')}
                          </S.ValueBreakdownText>
                          <S.ValueBreakdownChip>
                            {currencySymbol}
                            {valueByType.maintenance.toLocaleString()}
                          </S.ValueBreakdownChip>
                        </S.ValueBreakdownItem>
                      )}
                      {countByType.energy > 0 && (
                        <S.ValueBreakdownItem $type="energy">
                          <S.ValueBreakdownLine $type="energy" />
                          <S.ValueBreakdownText $type="energy">
                            {countByType.energy} {t('Energy')}
                          </S.ValueBreakdownText>
                          <S.ValueBreakdownChip>
                            {currencySymbol}
                            {valueByType.energy.toLocaleString()}
                          </S.ValueBreakdownChip>
                        </S.ValueBreakdownItem>
                      )}
                    </S.ValueBreakdownItems>
                  </S.ValueBreakdown>
                )}
            </S.TabsHeader>
          </S.StickyHeaderSection>

          <S.InsightsGridWrapper $headerHeight={headerHeight} $isHeaderStuck={isHeaderStuck}>
            <S.InsightsGrid>
              {insightsLoading ? (
                Array.from({ length: currentInsights.length || 6 }).map((_, index) => (
                  <S.InsightCardSkeleton key={`skeleton-${index}`}>
                    <div
                      style={{ height: '300px', background: token.colorBgElevated, borderRadius: token.borderRadiusLG }}
                    />
                  </S.InsightCardSkeleton>
                ))
              ) : currentInsights.length === 0 ? (
                <EmptyInsightsState activeTab={activeTab} />
              ) : (
                <>
                  {currentInsights.map(insight => {
                    let actionButtons: ActionButton[] = [];
                    let additionalActionButtons: AdditionalAction[] = [];

                    // Different actions based on active tab
                    if (activeTab === 'insight') {
                      // Available insights tab
                      actionButtons = [
                        {
                          label: t('Review & Publish'),
                          onClick: () => onReviewPublish?.(insight.id),
                        },
                        {
                          label: t('Open Opportunity'),
                          onClick: () => {
                            // Void for now
                          },
                        },
                      ];

                      additionalActionButtons = [
                        {
                          key: 'reject',
                          label: t('Reject (Report as Incorrect)'),
                          onClick: async () => {
                            if (insightsStore) {
                              await insightsStore.updateAssistantInsight(insight.id, {
                                status: InsightStatus.Reject,
                              });
                            }
                          },
                        },
                        {
                          key: 'archive',
                          label: t('Archive (Future Potential)'),
                          onClick: async () => {
                            if (insightsStore) {
                              await insightsStore.updateAssistantInsight(insight.id, {
                                status: InsightStatus.Archived,
                              });
                            }
                          },
                        },
                      ];
                    } else if (activeTab === 'published') {
                      // Published insights tab
                      actionButtons = [
                        {
                          label: t('Open Opportunity'),
                          onClick: () => {
                            // Void for now
                          },
                        },
                      ];

                      additionalActionButtons = [
                        {
                          key: 'edit',
                          label: t('Edit'),
                          onClick: () => onEditInsight?.(insight.id),
                        },
                      ];
                    } else if (activeTab === 'archived') {
                      // Archived insights tab - no action buttons
                      actionButtons = [];

                      additionalActionButtons = [
                        {
                          key: 'unarchive',
                          label: t('Unarchive'),
                          onClick: async () => {
                            if (insightsStore) {
                              // Unarchive by updating status to Draft
                              await insightsStore.updateAssistantInsight(insight.id, {
                                status: InsightStatus.Draft,
                              });
                            }
                          },
                        },
                        {
                          key: 'reject',
                          label: t('Reject (Report as Incorrect)'),
                          onClick: async () => {
                            if (insightsStore) {
                              await insightsStore.updateAssistantInsight(insight.id, {
                                status: InsightStatus.Reject,
                              });
                            }
                          },
                        },
                      ];
                    }

                    return (
                      <AssistantInsightCard
                        key={insight.id}
                        insight={insight}
                        onClick={() => handleInsightCardClick(insight.id)}
                        actionButtons={actionButtons}
                        additionalActionButtons={additionalActionButtons}
                      />
                    );
                  })}
                </>
              )}
            </S.InsightsGrid>
          </S.InsightsGridWrapper>
        </S.InsightsContainer>
      </>
    );
  },
);

AssistantInsights.displayName = 'AssistantInsights';

export default AssistantInsights;
